/**
 * A chat's menu (07 Workshop, rows 7 and 8): Rename, Move to… (the projects in
 * the person's order, the current one ticked, then New project…), Share… and
 * Delete…, the last in the alert ink. Someone else's shared chat offers Leave.
 * On a phone the same actions rise as a sheet headed by the chat.
 */
import { useState, type ReactNode } from "react";
import {
  Button,
  Menu,
  MenuItem,
  MenuTrigger,
  Popover,
  SubmenuTrigger,
  type Key,
} from "react-aria-components";
import {
  Check,
  Ellipsis,
  Folder,
  FolderInput,
  FolderPlus,
  LogOut,
  Pencil,
  Share,
  Trash2,
  type LucideIcon,
} from "lucide-react";
import { KitModal } from "@/app/routes/dashboard/components/kit/Modal";
import { useCoShellPortal } from "@/app/routes/dashboard/CoShellPortalContext";
import { cn } from "@/utils/helpers";
import type { WorkshopProject } from "@/types/workshop.type";

export type ChatMenuAction = "rename" | "share" | "delete" | "leave";

type Item = { id: ChatMenuAction | "move"; label: string; icon: LucideIcon; danger?: boolean; disabled?: boolean };

type Props = {
  title: string;
  /** "SEP 24 · 14:22 · ONLY YOU" under a phone sheet's title. */
  meta?: string;
  isOwner: boolean;
  /** Rename, move and share need write access to a live project. */
  canEdit: boolean;
  /** A shared chat can't move (the API refuses it). */
  canMove: boolean;
  shareDisabled?: boolean;
  deleteDisabled?: boolean;
  /** Where the chat is, and where it could go, in the person's order. */
  projectId: string;
  projects: WorkshopProject[];
  isPhone: boolean;
  onAction: (action: ChatMenuAction) => void;
  onMove: (projectId: string) => void;
  onMoveToNewProject: () => void;
  /** The trigger's look: the sidebar row's small ⋯ or the chat header's. */
  triggerClassName: string;
  /** Called as the menu opens and closes (the sidebar keeps its row lit). */
  onOpenChange?: (open: boolean) => void;
};

/** Move to… is withheld until moving a chat is fixed end to end; flip this back on then. */
const MOVE_ENABLED = false;

function itemsFor({ isOwner, canEdit, canMove, shareDisabled, deleteDisabled }: Props): Item[] {
  if (!isOwner) return [{ id: "leave", label: "Leave", icon: LogOut, danger: true }];
  return [
    ...(canEdit
      ? [
          { id: "rename" as const, label: "Rename", icon: Pencil },
          ...(MOVE_ENABLED && canMove ? [{ id: "move" as const, label: "Move to…", icon: FolderInput }] : []),
          { id: "share" as const, label: "Share…", icon: Share, disabled: shareDisabled },
        ]
      : []),
    { id: "delete", label: "Delete…", icon: Trash2, danger: true, disabled: deleteDisabled },
  ];
}

const ROW = "ws-menu-row";

function Row({ icon: Icon, children, danger }: { icon: LucideIcon; children: ReactNode; danger?: boolean }) {
  return (
    <>
      <Icon className="ws-menu-row-icon" aria-hidden="true" />
      <span className={cn("ws-menu-row-label", danger && "is-danger")}>{children}</span>
    </>
  );
}

export default function WorkshopChatMenu(props: Props) {
  const { title, meta, isPhone, onAction, onMove, onMoveToNewProject, projectId, projects } = props;
  const portal = useCoShellPortal();
  const [sheet, setSheet] = useState<"actions" | "move" | null>(null);
  const items = itemsFor(props);
  const label = `Chat actions for ${title}`;

  if (isPhone) {
    const run = (action: ChatMenuAction) => {
      setSheet(null);
      onAction(action);
    };
    return (
      <>
        <button
          type="button"
          className={props.triggerClassName}
          aria-label={label}
          aria-haspopup="dialog"
          onClick={() => {
            props.onOpenChange?.(true);
            setSheet("actions");
          }}
        >
          <Ellipsis aria-hidden="true" />
        </button>
        <KitModal
          isOpen={sheet !== null}
          onOpenChange={(open) => {
            if (!open) {
              setSheet(null);
              props.onOpenChange?.(false);
            }
          }}
          aria-label={sheet === "move" ? `Move ${title} to` : label}
          hideCloseButton
          modalClassName="ws-dialog ws-action-sheet"
          overlayClassName="ws-dialog-overlay"
        >
          <span className="ws-sheet-grabber" aria-hidden="true" />
          <div className="ws-action-sheet-head">
            <p className="ws-action-sheet-title">{sheet === "move" ? "Move to" : title}</p>
            {sheet === "move" ? null : meta ? <p className="ws-action-sheet-meta">{meta}</p> : null}
          </div>
          <div className="ws-action-sheet-list">
            {sheet === "move" ? (
              <>
                {projects.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    className="ws-action-sheet-item"
                    aria-current={p.id === projectId || undefined}
                    onClick={() => {
                      setSheet(null);
                      if (p.id !== projectId) onMove(p.id);
                    }}
                  >
                    <Folder aria-hidden="true" />
                    <span className="flex-1 truncate text-start">{p.name}</span>
                    {p.id === projectId ? <Check className="ws-action-sheet-check" aria-hidden="true" /> : null}
                  </button>
                ))}
                <button
                  type="button"
                  className="ws-action-sheet-item"
                  onClick={() => {
                    setSheet(null);
                    onMoveToNewProject();
                  }}
                >
                  <FolderPlus aria-hidden="true" />
                  New project…
                </button>
              </>
            ) : (
              items.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className={cn("ws-action-sheet-item", item.danger && "is-danger")}
                  disabled={item.disabled}
                  onClick={() => (item.id === "move" ? setSheet("move") : run(item.id))}
                >
                  <item.icon aria-hidden="true" />
                  {item.label}
                </button>
              ))
            )}
          </div>
        </KitModal>
      </>
    );
  }

  return (
    <MenuTrigger onOpenChange={props.onOpenChange}>
      <Button aria-label={label} className={props.triggerClassName}>
        <Ellipsis aria-hidden="true" />
      </Button>
      <Popover
        placement="bottom start"
        offset={6}
        UNSTABLE_portalContainer={portal ?? undefined}
        className="ws-menu-pop"
      >
        <Menu
          aria-label={label}
          className="ws-menu-list"
          disabledKeys={items.filter((i) => i.disabled).map((i) => i.id)}
          onAction={(key: Key) => {
            if (key !== "move") onAction(key as ChatMenuAction);
          }}
        >
          {items.map((item) =>
            item.id === "move" ? (
              <SubmenuTrigger key="move">
                <MenuItem id="move" textValue={item.label} className={ROW}>
                  <Row icon={item.icon}>{item.label}</Row>
                </MenuItem>
                <Popover
                  offset={6}
                  crossOffset={-6}
                  UNSTABLE_portalContainer={portal ?? undefined}
                  className="ws-menu-pop ws-menu-pop--wide"
                >
                  <Menu
                    aria-label={`Move ${title} to`}
                    className="ws-menu-list"
                    onAction={(key: Key) => {
                      if (key === "__new") onMoveToNewProject();
                      else if (key !== projectId) onMove(String(key));
                    }}
                  >
                    {projects.map((p) => (
                      <MenuItem
                        key={p.id}
                        id={p.id}
                        textValue={p.name}
                        className={cn(ROW, p.id === projectId && "is-current")}
                      >
                        <Row icon={Folder}>{p.name}</Row>
                        {p.id === projectId ? <Check className="ws-menu-row-check" aria-hidden="true" /> : null}
                      </MenuItem>
                    ))}
                    <MenuItem id="__new" textValue="New project…" className={ROW}>
                      <Row icon={FolderPlus}>New project…</Row>
                    </MenuItem>
                  </Menu>
                </Popover>
              </SubmenuTrigger>
            ) : (
              <MenuItem key={item.id} id={item.id} textValue={item.label} className={ROW}>
                <Row icon={item.icon} danger={item.danger}>
                  {item.label}
                </Row>
              </MenuItem>
            ),
          )}
        </Menu>
      </Popover>
    </MenuTrigger>
  );
}
